import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Gift, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const LightExitPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  const showPopup = useCallback(() => {
    const shown = sessionStorage.getItem('exit-popup-shown');
    if (!shown) {
      setIsVisible(true);
      sessionStorage.setItem('exit-popup-shown', 'true');
    }
  }, []);

  useEffect(() => {
    // Handle mouse leaving viewport (desktop exit intent)
    // Using mouseout on documentElement is more reliable than mouseleave on document
    const handleMouseOut = (e: MouseEvent) => {
      // Check if cursor is actually leaving the document (not just entering a child element)
      const target = e.relatedTarget as Node | null;
      
      // If relatedTarget is null or outside the document, cursor is leaving the page
      if (!target || !document.documentElement.contains(target)) {
        // Additional check: cursor leaving from top or left
        if (e.clientY <= 50 || e.clientX <= 0) {
          console.log('Exit intent triggered:', { clientX: e.clientX, clientY: e.clientY });
          showPopup();
        }
      }
    };

    // Handle browser back button
    const handlePopState = () => {
      const shown = sessionStorage.getItem('exit-popup-shown');
      if (!shown) {
        window.history.pushState(null, '', window.location.href);
        showPopup();
      }
    };

    window.history.pushState(null, '', window.location.href);
    document.documentElement.addEventListener('mouseout', handleMouseOut);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.documentElement.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showPopup]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleTryFree = () => {
    setIsVisible(false);
    navigate('/apply?plan=free');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
          />
          
          {/* Modal - Centered on all screens */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              x: [0, -8, 8, -6, 6, -4, 4, 0],
            }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ 
              type: "spring", 
              duration: 0.4,
              x: { duration: 0.5, delay: 0.2 }
            }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5 text-white relative">
                <button 
                  onClick={handleClose}
                  className="absolute top-3 right-3 p-2 hover:bg-white/20 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                <Gift className="w-10 h-10 mb-2" />
                <h3 className="text-xl font-bold">Wait! Before You Go...</h3>
                <p className="text-white/80 text-sm">We have something special</p>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="text-center mb-5">
                  <p className="text-2xl font-bold text-slate-900 mb-1">
                    Try 7 Days <span className="text-blue-600">FREE</span>
                  </p>
                  <p className="text-slate-600 text-sm">
                    Full access. No credit card.
                  </p>
                </div>

                <ul className="space-y-3 mb-5">
                  {[
                    "Academy 1 content",
                    "Video lessons",
                    "No credit card",
                    "Cancel anytime",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700 text-sm">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="space-y-3">
                  <Button
                    onClick={handleTryFree}
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-base font-semibold h-14 rounded-xl"
                  >
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  <button
                    onClick={handleClose}
                    className="w-full text-sm text-slate-400 hover:text-slate-600 py-3 min-h-[48px]"
                  >
                    No thanks, I'll pass
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LightExitPopup;
